import { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    IncomingVehicleInLoadPreview,
    OutgoingVehicleInLoadPreview,
    VehicleInUnannouncedLoadPreview,
} from '../../compoundManagement.types';
import { DisplayModelName } from '../dataDisplay/DisplayModelName';
import { DisplayDispatchType } from '../dataDisplay/DisplayDispatchType';
import { NoValue } from '../../../sharedComponents/NoValue';
import Button from '@rio-cloud/rio-uikit/Button';
import { DisplayMeansOfTransportType } from '../dataDisplay/DisplayMeansOfTransportType';

type BulkStatusUpdateReviewTableProps = {
    vehicles: IncomingVehicleInLoadPreview[] | VehicleInUnannouncedLoadPreview[] | OutgoingVehicleInLoadPreview[];
    onRemove: (vehicleId: string) => void;
};

export const BulkStatusUpdateReviewTable: FunctionComponent<BulkStatusUpdateReviewTableProps> = (props) => {
    return props.vehicles.length === 0 ? <EmptyReviewTable /> : <ReviewTable {...props} />;
};

const EmptyReviewTable = () => {
    return (
        <div className="display-flex align-items-center margin-bottom-20 justify-content-center">
            <span className="rioglyph rioglyph-info-sign text-size-20 margin-right-10" />
            <span className="text-light">
                <FormattedMessage id="outboundOrderBook.compoundManagement.bulkStatusUpdate.table.empty" />
            </span>
        </div>
    );
};

export const isVehicleWithArrival = (
    vehicle: IncomingVehicleInLoadPreview | VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview,
): vehicle is IncomingVehicleInLoadPreview => vehicle !== undefined && 'arrival' in vehicle;

export const isVehicleWithDeparture = (
    vehicle: IncomingVehicleInLoadPreview | VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview,
): vehicle is OutgoingVehicleInLoadPreview => vehicle !== undefined && 'departure' in vehicle;

const ReviewTable: FunctionComponent<BulkStatusUpdateReviewTableProps> = ({ vehicles, onRemove }) => {
    const hasArrival = isVehicleWithArrival(vehicles[0]);
    const hasDeparture = isVehicleWithDeparture(vehicles[0]);

    return (
        <div className="height-300 overflow-y-scroll">
            <div className="text-center margin-bottom-20">
                <span className="text-light">
                    <FormattedMessage
                        id="outboundOrderBook.compoundManagement.bulkStatusUpdate.table.title"
                        values={{ count: vehicles.length }}
                    />
                </span>
            </div>
            <table className="table table-layout-fixed table-column-overflow-hidden table-sticky-in-container table-hover table-head-filled margin-bottom-20">
                <thead>
                    <ReviewTableHeader hasTransportInformation={hasArrival || hasDeparture} />
                </thead>
                <tbody>
                    {vehicles.map((vehicle) => (
                        <ReviewTableRow key={vehicle.id} vehicle={vehicle} onRemove={onRemove} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ReviewTableHeader = (props: { hasTransportInformation: boolean }) => (
    <tr>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.common.heading.vin" />
        </th>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.common.heading.manufacturer" />
        </th>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.common.heading.model" />
        </th>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.common.heading.vehicleType" />
        </th>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.compoundManagement.table.heading.modelVersion" />
        </th>
        <th className="ellipsis-1">
            <FormattedMessage id="outboundOrderBook.compoundManagement.sidebar.finalDestination" />
        </th>
        {props.hasTransportInformation && (
            <>
                <th className="ellipsis-1">
                    <FormattedMessage id="outboundOrderBook.common.heading.meansOfTransportType" />
                </th>
                <th className="ellipsis-1">
                    <FormattedMessage id="outboundOrderBook.common.heading.meansOfTransport" />
                </th>
            </>
        )}
        <th className="text-right ellipsis-1">
            <FormattedMessage id="outboundOrderBook.common.heading.removeAction" />
        </th>
    </tr>
);

type ReviewTableRowProps = {
    vehicle: VehicleInUnannouncedLoadPreview | OutgoingVehicleInLoadPreview;
    onRemove: (vehicleId: string) => void;
};

const ReviewTableRow: FunctionComponent<ReviewTableRowProps> = ({ vehicle, onRemove }: ReviewTableRowProps) => {
    const intl = useIntl();
    const spanOrNoValue = (text: string | undefined) => (text ? <span>{text}</span> : <NoValue />);

    const finalDestination = vehicle.vehicle.finalDestination;

    const transportInformation = isVehicleWithArrival(vehicle)
        ? vehicle.arrival.transportInformation
        : isVehicleWithDeparture(vehicle)
          ? vehicle.departure.transportInformation
          : undefined;

    return (
        <tr>
            <td className="ellipsis-1">{vehicle.vehicle.vin}</td>
            <td className="ellipsis-1">{spanOrNoValue(vehicle.vehicle.model.manufacturer)}</td>
            <td className="ellipsis-1">
                <DisplayModelName vehicle={vehicle.vehicle} />
            </td>
            <td className="ellipsis-1">
                <DisplayDispatchType vehicle={vehicle.vehicle} />
            </td>
            <td className="ellipsis-1">{spanOrNoValue(vehicle.vehicle.model.version)}</td>
            <td className="ellipsis-1">
                {spanOrNoValue(finalDestination?.identifier ?? finalDestination?.address?.countryCode)}
            </td>
            {transportInformation && (
                <>
                    <td className="ellipsis-1">
                        <DisplayMeansOfTransportType meansOfTransportType={transportInformation.meansOfTransportType} />
                    </td>
                    <td className="ellipsis-1">{spanOrNoValue(transportInformation.meansOfTransportId)}</td>
                </>
            )}
            <td className="text-right">
                <Button
                    type="button"
                    iconOnly
                    iconName="rioglyph-trash"
                    bsStyle={Button.DANGER}
                    variant={Button.VARIANT_LINK}
                    onClick={() => onRemove(vehicle.id)}
                    aria-label={intl.formatMessage({
                        id: 'outboundOrderBook.compoundManagement.bulkStatusUpdate.table.remove',
                    })}
                />
            </td>
        </tr>
    );
};
